import React, { useState } from 'react';
// @mui
import {
  Container,
  Box,
  Typography,
  Tabs,
  Tab,
  Stack,
  Button
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
// components
import Page from '../../components/Page';
import OverviewTab from '../../components/ocpi/commands/tabs/OverviewTab';
import AdvancedTab from '../../components/ocpi/commands/tabs/AdvancedTab';
import QueueMonitorTab from '../../components/ocpi/commands/tabs/QueueMonitorTab';
import CommandBuilderTab from '../../components/ocpi/commands/tabs/CommandBuilderTab';
import HistoryTab from '../../components/ocpi/commands/tabs/HistoryTab';
import MonitorTab from '../../components/ocpi/commands/tabs/MonitorTab';
import AnalyticsTab from '../../components/ocpi/commands/tabs/AnalyticsTab';
// hooks
import { useCommandData } from 'src/hooks/useOcpiCommands.hook';

const TABS = [
  { value: 'overview', label: 'Overview' },
  // { value: 'advanced', label: 'Advanced' },
  // { value: 'queue-monitor', label: 'Queue Monitor' },
  { value: 'command-builder', label: 'Command Builder' },
  { value: 'history', label: 'History' },
  // { value: 'monitor', label: 'Monitor' },
  // { value: 'analytics', label: 'Analytics' }
];

export default function OCPICommands() {
  const [activeTab, setActiveTab] = useState('overview');
  const theme = useTheme();
  const commandData = useCommandData();

  const surfaceStyles = {
    borderRadius: 2,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    backgroundImage:
      theme.palette.mode === 'dark'
        ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
        : 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(247,249,252,0.94) 100%)',
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 22px 60px rgba(0,0,0,0.55)'
        : theme.shadows[3]
  };

  const pageBackground =
    theme.palette.mode === 'dark'
      ? 'radial-gradient(circle at top, rgba(7,11,19,0.95) 0%, rgba(4,6,12,0.98) 55%)'
      : 'linear-gradient(180deg, rgba(248,250,252,0.9) 0%, rgba(255,255,255,1) 70%)';

  const handleRedirectToOverview = () => {
    setActiveTab('overview');
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewTab data={commandData} onSendCommand={commandData.sendQuickCommand} />;
      // case 'advanced':
      //   return <AdvancedTab data={commandData} />;
      // case 'queue-monitor':
      //   return <QueueMonitorTab data={commandData} />;
      case 'command-builder':
        return <CommandBuilderTab data={commandData} onRedirectToOverview={handleRedirectToOverview} />;
      case 'history':
        return <HistoryTab data={commandData} />;
      // case 'monitor':
      //   return <MonitorTab data={commandData} />;
      // case 'analytics':
      //   return <AnalyticsTab data={commandData} />;
      default:
        return <OverviewTab data={commandData} onSendCommand={commandData.sendQuickCommand} />;
    }
  };

  return (
    <Page title="OCPI Commands">
      <Box sx={{ backgroundImage: pageBackground, py: { xs: 2, md: 3 } }}>
        <Container maxWidth="xl">
          {/* Back Button */}
          <Stack sx={{ mb: 2 }} direction="row" alignItems="center">
            <Button
              variant="outlined"
              onClick={() => window.location.href = '/ocpi-management'}
              sx={{ mr: 2 }}
            >
              Back to OCPI Management
            </Button>
          </Stack>

          {/* Header */}
          <Box
            sx={{
              ...surfaceStyles,
              px: { xs: 2, md: 3 },
              py: { xs: 2, md: 2.5 },
              mb: 3
            }}
          >
            <Typography variant="h3" component="h1" gutterBottom>
              OCPI Commands
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Remote charging operations and control
            </Typography>
          </Box>

          {/* Tab Navigation and Content Container */}
          <Box
            sx={{
              ...surfaceStyles,
              overflow: 'hidden'
            }}
          >
            {/* Tab Navigation */}
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                backgroundColor: 'transparent',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  minHeight: 48,
                  color: 'text.secondary',
                  '&.Mui-selected': {
                    color: 'primary.main',
                    fontWeight: 600,
                  }
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                  backgroundImage: `linear-gradient(90deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`
                }
              }}
            >
              {TABS.map((tab) => (
                <Tab
                  key={tab.value}
                  label={tab.label}
                  value={tab.value}
                />
              ))}
            </Tabs>

            {/* Tab Content */}
            <Box
              sx={{
                minHeight: 600,
                p: 0,
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? alpha(theme.palette.common.white, 0.01)
                    : 'transparent'
              }}
            >
              {renderTabContent()}
            </Box>
          </Box>
        </Container>
      </Box>
    </Page>
  );
}
